// images.js
// Dynamically import images using require.context
const importAll = (context) => {
  const images = {};
  context.keys().forEach((key) => {
    const cleanKey = key.replace('./', '');
    images[cleanKey] = context(key);
  });
  return images;
};

// Import WebP images generated from the PNG originals (run `npm run images`)
const generatedImages = importAll(require.context('../content/images/generated', false, /\.webp$/));

// Import videos (full size and -grid versions)
const videos = importAll(require.context('../content/images/videos', false, /\.mp4$/));

// Import profile image
const profileImage = importAll(require.context('../content/images', false, /abby.png$/));

// Placeholder SVG for missing images
const missingImagePlaceholder = `data:image/svg+xml;base64,${btoa(`
  <svg xmlns="http://www.w3.org/2000/svg" width="240" height="240" viewBox="0 0 240 240">
    <rect width="100%" height="100%" fill="#F7F7F8"/>
  </svg>
`)}`;


// Mapping of project keys to image names
const projectImageMapping = {
  figmaAiGrowth: [
    'figma-ai-growth-1.png',
    'figma-ai-growth-2.png',
    'figma-ai-growth-3.png'
  ],
  figmaOnboarding: [
    'figma-onboarding-1.png',
    'figma-onboarding-2.png',
    'figma-onboarding-3.png'
  ],
  figmaStringManagement: [
    'figma-string-management-1.png',
    'figma-string-management-2.png',
    'figma-string-management-3.png'
  ],
  figmaSeatRequests: [
    'figma-seat-requests-1.mp4',
    'figma-seat-requests-2.png',
    'figma-seat-requests-3.mp4'
  ],
  figmaPaywall: [
    'figma-paywall-1.png',
    'figma-paywall-2.png',
    'figma-paywall-3.png'
  ],
  figmaSignup: [
    'figma-acquisition-3.png',
    'figma-signup-checkout.png',
    'figma-signup-3.png'
  ],
  figmaRetention: [
    'figma-retention-1.png',
    'figma-retention-2.mp4',
    'figma-retention-3.png'
  ],
  lyftAppRedesign: [
    'lyft-app-redesign-1.png',
    'lyft-app-redesign-2.png',
    'lyft-app-redesign-3.png',
  ],
  lyftFoundations: [
    'lyft-foundations-1.mp4',
    'lyft-foundations-2.png',
    'lyft-foundations-3.png',
  ],
  lyftGroupRides: [
    'lyft-group-rides-1.png',
    'lyft-group-rides-2.png',
    'lyft-group-rides-3.mp4',
  ],
  lyftActivation: [
    'lyft-activation-1.png',
    'lyft-activation-2.png',
    'lyft-activation-3.mp4',
  ],
  lyftRideshareExperiments: [
    'lyft-rideshare-experiments-1.mp4',
    'lyft-rideshare-experiments-2.png',
    'lyft-rideshare-experiments-3.mp4',
  ],
  lyftProductQuality: [
    'lyft-product-quality-1.png',
    'lyft-product-quality-2.png',
    'lyft-product-quality-3.png',
  ],
  bettermentAdminApp: [
    'betterment-admin-app-1.png',
    'betterment-admin-app-2.png',
    'betterment-admin-app-3.png',
  ],
  bettermentSalesFlow: [
    'betterment-sales-flow-1.png',
    'betterment-sales-flow-2.png',
    'betterment-sales-flow-3.png',
  ],
  bettermentParticipantXP: [
    'betterment-participant-xp-1.png',
    'betterment-participant-xp-2.png',
    'betterment-participant-xp-3.png',
  ],
  bettermentDesignSystem: [
    'betterment-design-system-1.png',
    'betterment-design-system-2.png',
    'betterment-design-system-3.png',
  ],
};

// Function to process media for a project
const processProjectMedia = (projectKey) => {
  return projectImageMapping[projectKey].map(filename => {
    if (filename.endsWith('.mp4')) {
      return {
        type: 'video',
        src: videos[filename.replace('.mp4', '-grid.mp4')] || missingImagePlaceholder,
        stackSrc: null,
        fullResSrc: videos[filename] || missingImagePlaceholder
      };
    }

    const name = filename.replace('.png', '');
    return {
      type: 'image',
      src: generatedImages[`${name}-grid.webp`] || missingImagePlaceholder,
      stackSrc: generatedImages[`${name}-stack.webp`] || null,
      fullResSrc: generatedImages[`${name}-full.webp`] || missingImagePlaceholder
    };
  });
};

// About page images, one per breakpoint (largest first)
const processAboutImages = () => {
  return ['about-1', 'about-2', 'about-3', 'about-4'].map(name => {
    const source = generatedImages[`${name}.webp`] || missingImagePlaceholder;
    return { type: 'image', src: source, fullResSrc: source };
  });
};

const processProfileImage = () => {
  return [
    'abby.png'
  ].map(filename => ({
    type: 'image',
    src: profileImage[filename] || missingImagePlaceholder,
    fullResSrc: profileImage[filename] || missingImagePlaceholder
  }));
};

// Generate images for each project
export const images = {
  ...Object.keys(projectImageMapping).reduce((acc, projectKey) => {
    acc[projectKey] = processProjectMedia(projectKey);
    return acc;
  }, {}),
  about: processAboutImages(),
  profile: processProfileImage()
};

// Fallback method for getting project images
export const getProjectImages = (projectKey) => {
  return images[projectKey] || [missingImagePlaceholder, missingImagePlaceholder, missingImagePlaceholder];
};

// Method to get a single image
images.getImage = (name, fallback) => {
  return generatedImages[`${name}-full.webp`] || generatedImages[`${name}.webp`] || profileImage[`${name}.png`] || fallback || missingImagePlaceholder;
};